import React, {
  createContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import './menuPage.scss'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfigProvider,
  Layout,
  Menu,
  Modal,
  Space,
  Switch,
  Tabs,
  theme,
  Tree,
  Tooltip
} from 'antd'

import AddModal from '../../Components/Modals/menu modal/addModal'
import { useSelector, useDispatch } from 'react-redux'
import { setLiveMenuCard } from '../../redux/userredux/menu_slice'
import {
  addCategory,
  addFoodItem,
  addFoodItemToCategory,
  addSubCategory,
  deleteCategory,
  deleteFoodItem,
  deleteSubCategory,
  fetchAddOns,
  findReqCategory,
  findReqSubCategory,
  getFoodList,
  getFoodListByCategory,
  updateItemStatus,
  getGroupsUsingFoodItem,
  getAddonFoodItems,
  resolveFoodItemById
} from '../../Utility/Menu/category.util'
import FoodItem from '../../Components/FoodItem/foodItem'
import { axiosInstance } from '../../interceptor/axios_interceptor'
import {
  CheckOutlined,
  CloseOutlined,
  MinusCircleTwoTone,
  PlusCircleTwoTone,
  CheckCircleOutlined,
  ShoppingCartOutlined,
  CoffeeOutlined
} from '@ant-design/icons'
import TimeSlotModal from '../../Components/Modals/timeSlot modal/timeSlotModal'
import AddonGroupModal from '../../Components/Modals/addonGroup modal/addonGroupModal'
import ScrollablePanel from '../../Components/ScrollablePanel/ScrollablePanel'

interface MenuContextProps {
  menu: any[]
  timeSlots: any[]
  addonGroups: any[]
}

export const MenuContext = React.createContext<MenuContextProps>({
  menu: [],
  timeSlots: [
    {
      slotName: 'All Day',
      availableHours: {
        from: '12:00 AM',
        to: '11:59 PM'
      },
      type: 'DEFAULT'
    }
  ],
  addonGroups: []
})

const { Content, Sider } = Layout
const MenuPage = () => {
  const userRedux = useSelector((state: any) => state.user)
  const vendorInfo = userRedux.vendorInfo
  const dispatch = useDispatch()

  // Live Menu State
  const [liveMenu, setLiveMenu] = useState<any[]>([])
  const [liveTimeSlots, setLiveTimeSlots] = useState<any[]>([])
  const [liveAddonGroups, setLiveAddonGroups] = useState<any[]>([])
  const [liveVersionId, setLiveVersionId] = useState<string | null>(null)

  // Draft Menu State
  const [draftMenu, setDraftMenu] = useState<any[]>([])
  const [draftTimeSlots, setDraftTimeSlots] = useState<any[]>([])
  const [draftAddonGroups, setDraftAddonGroups] = useState<any[]>([])
  const [draftVersionId, setDraftVersionId] = useState<string | null>(null)
  const [draftStatus, setDraftStatus] = useState<
    'DRAFT' | 'PENDING_APPROVAL' | 'REJECTED' | null
  >(null)
  const [rejectionReason, setRejectionReason] = useState<string | null>(null)

  // Active Tab State
  const [activeTab, setActiveTab] = useState<'live' | 'draft'>('live')

  // Active menu references (based on activeTab)
  const menu = activeTab === 'draft' ? draftMenu : liveMenu
  const setMenu = activeTab === 'draft' ? setDraftMenu : setLiveMenu
  const timeSlots = activeTab === 'draft' ? draftTimeSlots : liveTimeSlots
  const setTimeSlots =
    activeTab === 'draft' ? setDraftTimeSlots : setLiveTimeSlots
  const addonGroups = activeTab === 'draft' ? draftAddonGroups : liveAddonGroups
  const setAddonGroups =
    activeTab === 'draft' ? setDraftAddonGroups : setLiveAddonGroups

  const [modalVisible, setModalVisible] = useState(false)
  const [modalTimeSlotVisible, setModalTimeSlotVisible] = useState(false)
  const [modalAddonGroupVisible, setModalAddonGroupVisible] = useState(false)
  const [modalType, setModalType] = useState({})
  const [foodList, setFoodList] = useState([])
  const [selectedFood, setSelectedFood] = useState<undefined | null | object>(
    null
  )
  const [selectedKeys, setSelectedKeys] = useState([])
  const restaurantLive = useSelector((state: any) => state.restaurant?.live)
  const isRestaurantActive =
    restaurantLive === 'LIVE' || restaurantLive === 'SUSPENDED'

  //sidecollapase state
  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken()

  // Edit permissions: Live tab is always read-only, Draft tab is editable only when restaurant is not live
  const canEdit = activeTab === 'draft' && !selectedFood

  // Helper to compute disabled items in live menu as a tree
  const disabledMenuTree = useMemo(() => {
    if (activeTab !== 'live') return []
    const tree: any[] = []
    liveMenu.forEach((cat: any) => {
      const catCopy = { ...cat, children: [] }
      let hasDisabledInCat = false

      if (cat.hasSubCategory) {
        cat.children?.forEach((subCat: any) => {
          const subCatCopy = { ...subCat, foodItems: [] }
          let hasDisabledInSubCat = false
          subCat.foodItems?.forEach((item: any) => {
            if (item.status === 'Not Available') {
              subCatCopy.foodItems.push(item)
              hasDisabledInSubCat = true
              hasDisabledInCat = true
            }
          })
          if (hasDisabledInSubCat) {
            catCopy.children.push(subCatCopy)
          }
        })
      } else {
        cat.children?.forEach((item: any) => {
          if (item.status === 'Not Available') {
            catCopy.children.push(item)
            hasDisabledInCat = true
          }
        })
      }
      if (hasDisabledInCat) {
        tree.push(catCopy)
      }
    })
    return tree
  }, [liveMenu, activeTab])

  // Helper for bulk updating status
  const updateBulkOrderStatus = async (itemIds: string[], newStatus: string) => {
    if (activeTab !== 'live' || itemIds.length === 0) return

    try {
      const response = await axiosInstance.patch(`/api/vendor/menu/restaurant/${vendorInfo.restaurantId}/items/bulk-status`, {
        itemIds,
        status: newStatus
      })

      if (response.data.responseCode === 0) {
        // Update local state
        const updatedMenu = (liveMenu as any[]).map((cat) => {
          if (cat.hasSubCategory) {
            return {
              ...cat,
              children: cat.children.map((subCat: any) => {
                return {
                  ...subCat,
                  foodItems: subCat.foodItems?.map((item: any) =>
                    itemIds.includes(item.food_id) ? { ...item, status: newStatus } : item
                  )
                }
              })
            }
          } else {
            return {
              ...cat,
              children: cat.children?.map((item: any) =>
                itemIds.includes(item.food_id) ? { ...item, status: newStatus } : item
              )
            }
          }
        })
        setLiveMenu(updatedMenu as never[])
        
        // Also update foodList if currently displayed
        setFoodList((prevList: any) => 
          prevList.map((item: any) => 
            itemIds.includes(item.food_id) ? { ...item, status: newStatus } : item
          )
        )
        
        Modal.success({ content: `Successfully updated ${itemIds.length} items to ${newStatus}` })
      } else {
        Modal.error({ title: 'Failed to Update Status', content: response.data.message })
      }
    } catch (error: any) {
      console.error('Error updating bulk status:', error)
      Modal.error({
        title: 'Error Updating Status',
        content: error.response?.data?.message || 'An unexpected error occurred'
      })
    }
  }

  // Toggle Category Status
  const toggleCategoryStatus = (event: any, categoryId: string, currentStatus: boolean) => {
    event.stopPropagation()
    const newStatus = currentStatus ? 'Not Available' : 'Available'
    const cat = liveMenu.find((c: any) => c.category_id === categoryId)
    if (!cat) return
    const itemIds: string[] = []
    if (cat.hasSubCategory) {
      cat.children?.forEach((subCat: any) => {
        subCat.foodItems?.forEach((item: any) => itemIds.push(item.food_id))
      })
    } else {
      cat.children?.forEach((item: any) => itemIds.push(item.food_id))
    }
    updateBulkOrderStatus(itemIds, newStatus)
  }

  // Toggle Subcategory Status
  const toggleSubCategoryStatus = (event: any, categoryId: string, subCategoryId: string, currentStatus: boolean) => {
    event.stopPropagation()
    const newStatus = currentStatus ? 'Not Available' : 'Available'
    const cat = liveMenu.find((c: any) => c.category_id === categoryId)
    if (!cat || !cat.hasSubCategory) return
    const subCat = cat.children?.find((s: any) => s.subcategory_id === subCategoryId)
    if (!subCat) return
    const itemIds: string[] = []
    subCat.foodItems?.forEach((item: any) => itemIds.push(item.food_id))
    updateBulkOrderStatus(itemIds, newStatus)
  }

  // Check if category is fully available
  const isCategoryAvailable = (categoryId: string) => {
    const cat = liveMenu.find((c: any) => c.category_id === categoryId)
    if (!cat) return false
    let allAvailable = true
    let hasItems = false
    if (cat.hasSubCategory) {
      cat.children?.forEach((subCat: any) => {
        subCat.foodItems?.forEach((item: any) => {
          hasItems = true
          if (item.status === 'Not Available') allAvailable = false
        })
      })
    } else {
      cat.children?.forEach((item: any) => {
        hasItems = true
        if (item.status === 'Not Available') allAvailable = false
      })
    }
    return hasItems ? allAvailable : false
  }

  // Check if subcategory is fully available
  const isSubCategoryAvailable = (categoryId: string, subCategoryId: string) => {
    const cat = liveMenu.find((c: any) => c.category_id === categoryId)
    if (!cat || !cat.hasSubCategory) return false
    const subCat = cat.children?.find((s: any) => s.subcategory_id === subCategoryId)
    if (!subCat) return false
    let allAvailable = true
    let hasItems = false
    subCat.foodItems?.forEach((item: any) => {
      hasItems = true
      if (item.status === 'Not Available') allAvailable = false
    })
    return hasItems ? allAvailable : false
  }

  const add = (event: any, type: string, key: any, subKey: any) => {
    console.log(event, type, key, subKey)
    setModalVisible(true)
    setModalType({ type, key, subKey })
  }

  const deleteMenuItems = (
    event: any,
    type: string,
    category: any,
    subCategory: any,
    foodItem: any
  ) => {
    console.log(event, type, category, subCategory, foodItem)

    // Find the entity name
    let entityName = ''
    if (type === 'Category') {
      const cat = (menu as any[]).find((c: any) => c.category_id === category)
      entityName = cat?.label || 'this category'
    } else if (type === 'SubCategory') {
      const cat = (menu as any[]).find((c: any) => c.category_id === category)
      const subCat = cat?.children?.find(
        (s: any) => s.subcategory_id === subCategory
      )
      entityName = subCat?.label || 'this subcategory'
    } else if (type === 'FoodItem') {
      const cat = (menu as any[]).find((c: any) => c.category_id === category)
      let item
      if (cat?.hasSubCategory) {
        const subCat = cat.children?.find(
          (s: any) => s.subcategory_id === subCategory
        )
        item = subCat?.foodItems?.find((f: any) => f.food_id === foodItem)
      } else {
        item = cat?.children?.find((f: any) => f.food_id === foodItem)
      }
      entityName = item?.label || 'this food item'
    }

    // Show confirmation modal
    Modal.confirm({
      title: `Delete ${type === 'SubCategory' ? 'Subcategory' : type}?`,
      content: `Are you sure you want to delete ${type === 'SubCategory' ? 'subcategory' : type.toLowerCase()} "${entityName}"?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        // Check if trying to delete a food item that's used as an addon
        if (type === 'FoodItem') {
          const usage = getGroupsUsingFoodItem(foodItem, addonGroups)
          if (usage.groupNames.length > 0) {
            Modal.error({
              title: 'Cannot Delete Food Item',
              content: `This item is being used as an addon in: ${usage.groupNames.join(', ')}. Please remove it from these groups first.`
            })
            return
          }
        }

        if (type === 'Category') {
          setMenu(deleteCategory(menu, category))
          setFoodList([])
        } else if (type === 'SubCategory') {
          setMenu(deleteSubCategory(menu, category, subCategory))
          setFoodList([])
        } else if (type === 'FoodItem') {
          setMenu(deleteFoodItem(menu, category, subCategory, foodItem))
          console.log('selectedKey', selectedKeys)
          if (selectedKeys.length > 1) {
            const reqCategory = findReqSubCategory(
              menu,
              selectedKeys[0],
              selectedKeys[1],
              'label'
            )
            setFoodList(reqCategory.foodItems)
          } else {
            setFoodList(getFoodListByCategory(menu, selectedKeys[0]))
          }
        }
        console.log(menu)
      }
    })
  }

  const handleCancel = () => {
    setModalVisible(false)
    setModalTimeSlotVisible(false)
    setModalAddonGroupVisible(false)
  }

  const updateOrderStatus = async (
    event: any,
    type: string,
    category: any,
    subCategory: any,
    foodItem: any
  ) => {
    if (activeTab === 'live') {
      // For live menu, make API call to update item status
      try {
        // Find current status and toggle it
        let currentStatus = 'Available'

        // Find the food item in the menu to get its current status
        for (const cat of liveMenu as any[]) {
          console.log('Checking Category:', cat.category_id, category)
          if (cat.category_id === category) {
            if (cat.hasSubCategory) {
              for (const subCat of cat.children) {
                if (subCat.subcategory_id === subCategory) {
                  const item = subCat.foodItems?.find(
                    (f: any) => f.food_id === foodItem
                  )
                  if (item) {
                    console.log('Found Item:', item)
                    currentStatus = item.status
                    break
                  }
                }
              }
            } else {
              const item = cat.children?.find(
                (f: any) => f.food_id === foodItem
              )
              if (item) {
                console.log('Found Item2:', item)
                currentStatus = item.status
              }
            }
          }
        }
        console.log('Current Status:', currentStatus)
        const newStatus =
          currentStatus === 'Available' ? 'Not Available' : 'Available'

        const response = await axiosInstance.patch(
          `/api/vendor/menu/item/${foodItem}/status`,
          { status: newStatus }
        )

        if (response.data.responseCode === 0) {
          // Update local state with the new status
          const updatedMenu = (liveMenu as any[]).map((cat) => {
            if (cat.category_id === category) {
              if (cat.hasSubCategory) {
                return {
                  ...cat,
                  children: cat.children.map((subCat: any) => {
                    if (subCat.subcategory_id === subCategory) {
                      return {
                        ...subCat,
                        foodItems: subCat.foodItems?.map((item: any) =>
                          item.food_id === foodItem
                            ? { ...item, status: newStatus }
                            : item
                        )
                      }
                    }
                    return subCat
                  })
                }
              } else {
                return {
                  ...cat,
                  children: cat.children?.map((item: any) =>
                    item.food_id === foodItem
                      ? { ...item, status: newStatus }
                      : item
                  )
                }
              }
            }
            return cat
          })
          setLiveMenu(updatedMenu as never[])
          setFoodList((prevList: any) => 
            prevList.map((item: any) => 
              item.food_id === foodItem ? { ...item, status: newStatus } : item
            )
          )
          Modal.success({
            content: `Item status updated to ${newStatus}`
          })
        } else {
          Modal.error({
            title: 'Failed to Update Status',
            content:
              response.data.message ||
              'An error occurred while updating item status'
          })
        }
      } catch (error: any) {
        console.error('Error updating item status:', error)
        Modal.error({
          title: 'Error Updating Status',
          content:
            error.response?.data?.message ||
            error.message ||
            'An unexpected error occurred'
        })
      }
    }
  }

  const menuAdd = (props?: any) => {
    console.log(props)
    setModalVisible(false)
    if (props.type === 'Category') {
      setMenu(addCategory(menu, props.category))
    } else if (props.type === 'SubCategory/FoodItem') {
      if (props.subCategory) {
        setMenu(addSubCategory(menu, props.category, props.subCategory))
      } else if (props.foodItem) {
        addFoodItemToCategory(
          menu,
          props.category,
          props.foodItem,
          vendorInfo.workingDaysAndHours
        )
      }
    } else if (props.type === 'FoodItem') {
      setMenu(
        addFoodItem(
          menu,
          props.category,
          props.subCategory,
          props.foodItem,
          vendorInfo.workingDaysAndHours
        )
      )
    }
    console.log(menu)
  }

  useEffect(() => {
    const loadMenus = async () => {
      try {
        // Fetch both live and draft menus in parallel
        const [liveResponse, draftResponse] = await Promise.all([
          axiosInstance.get(`/api/vendor/menu/live/${vendorInfo.restaurantId}`),
          axiosInstance.get(`/api/vendor/menu/draft/${vendorInfo.restaurantId}`)
        ])

        console.log('Live menu response:', liveResponse.data)
        console.log('Draft menu response:', draftResponse.data)

        // Load live menu
        if (
          liveResponse.data.responseCode === 0 &&
          liveResponse.data.data?.versionId
        ) {
          setLiveVersionId(liveResponse.data.data.versionId)
          setLiveMenu(liveResponse.data.data.menu.menuCard || [])
          dispatch(setLiveMenuCard(liveResponse.data.data.menu.menuCard || []))
          setLiveTimeSlots(liveResponse.data.data.menu.timeSlots || [])
          setLiveAddonGroups(liveResponse.data.data.menu.addonGroups || [])
        }

        // Load draft menu
        if (
          draftResponse.data.responseCode === 0 &&
          draftResponse.data.data?.versionId
        ) {
          setDraftVersionId(draftResponse.data.data.versionId)
          setDraftMenu(draftResponse.data.data.menuCard || [])
          setDraftTimeSlots(
            draftResponse.data.data.timeSlots || [
              {
                slotName: 'All Day',
                availableHours: {
                  from: '12:00 AM',
                  to: '11:59 PM'
                },
                type: 'DEFAULT'
              }
            ]
          )
          setDraftAddonGroups(draftResponse.data.data.addonGroups || [])
          setDraftStatus(draftResponse.data.data.status || null)
          setRejectionReason(draftResponse.data.data.rejectionReason || null)
        } else {
          // No draft exists
          setDraftVersionId(null)
          setDraftMenu([])
          setDraftTimeSlots([
            {
              slotName: 'All Day',
              availableHours: {
                from: '12:00 AM',
                to: '11:59 PM'
              },
              type: 'DEFAULT'
            }
          ])
          setDraftAddonGroups([])
          setDraftStatus(null)
          setRejectionReason(null)
        }
      } catch (error) {
        console.error('Error loading menus:', error)
      }
    }

    if (vendorInfo.restaurantId) {
      loadMenus()
    }
  }, [vendorInfo.restaurantId])

  const siderStyle: React.CSSProperties = {
    height: '100%',
    boxShadow: '1px 1px 6px 2px #80808073',
    borderRadius: '5px',
    paddingBottom: '10px'
  }

  const foodSiderStyle: React.CSSProperties = {
    height: '100%',
    boxShadow: '1px 1px 6px 2px #80808073',
    borderRadius: '5px'
  }

  const categorySelected = ({ keyPath }: any) => {
    setSelectedKeys(keyPath)
    keyPath.reverse()
    console.log(keyPath)
    const reqCategory = findReqCategory(menu, keyPath[0], 'label')
    if (reqCategory.hasSubCategory) {
      setFoodList(getFoodList(menu, keyPath[0], keyPath[1]))
    } else {
      console.log('yoyo')
      if (reqCategory.children.length > 0) {
        setFoodList(getFoodListByCategory(menu, keyPath[0]))
      }
      console.log('yoyo', foodList)
    }
  }

  const changeTimeSlots = (timeSlots: any) => {
    setTimeSlots(timeSlots)
    setModalTimeSlotVisible(false)
  }

  const foodSelected = ({ key }: any) => {
    setSelectedFood(foodList.find((foodItem: any) => foodItem.key === key))
  }

  const onUpdate = () => {
    setSelectedFood(null)
  }

  const getAddOns = () => {
    return addonGroups
  }

  const getAddonFoodItemsFromMenu = () => {
    return getAddonFoodItems(menu)
  }

  const resolveFoodItem = (foodId: string) => {
    return resolveFoodItemById(menu, foodId)
  }

  const checkAddonUsage = (foodId: string) => {
    return getGroupsUsingFoodItem(foodId, addonGroups)
  }

  const cloneFromLiveMenu = () => {
    Modal.confirm({
      title: 'Clone Live Menu to Draft?',
      content:
        'This will copy the current live menu to your draft. Any existing draft data will be replaced.',
      onOk: async () => {
        try {
          const payload = {
            restaurantId: vendorInfo.restaurantId,
            addonGroups: liveAddonGroups,
            menuCard: liveMenu,
            timeSlots: liveTimeSlots
          }
          const response = await axiosInstance.post(
            '/api/vendor/menu/create-draft',
            payload
          )

          if (response.data.responseCode === 0) {
            const versionData = response.data.data
            setDraftMenu(structuredClone(liveMenu))
            setDraftTimeSlots(structuredClone(liveTimeSlots))
            setDraftAddonGroups(structuredClone(liveAddonGroups))
            setDraftVersionId(versionData.versionId)
            setDraftStatus(versionData.status)
            setRejectionReason(null)
            Modal.success({
              content: 'Live menu cloned successfully!'
            })
          } else {
            Modal.error({
              title: 'Failed to Clone Menu',
              content: response.data.message || 'An error occurred'
            })
          }
        } catch (error: any) {
          Modal.error({
            title: 'Failed to Clone Menu',
            content: error.response?.data?.message || 'An error occurred'
          })
        }
      }
    })
  }

  const clearDraftMenu = () => {
    Modal.confirm({
      title: 'Clear Draft Menu?',
      content:
        liveMenu.length > 0
          ? 'This will clear all draft data. You can then clone from the live menu.'
          : 'This will clear all draft data. You can then start from scratch.',
      onOk: async () => {
        try {
          if (draftVersionId) {
            await axiosInstance.delete(`/api/vendor/menu/draft/${draftVersionId}`)
          }
          setDraftMenu([])
          setDraftTimeSlots([])
          setDraftAddonGroups([])
          setDraftVersionId(null)
          setDraftStatus(null)
          setRejectionReason(null)
          Modal.success({
            content: 'Draft menu cleared successfully!'
          })
        } catch (error: any) {
          Modal.error({
            title: 'Failed to Clear Draft',
            content: error.response?.data?.message || 'An error occurred'
          })
        }
      }
    })
  }

  const updateMenuCard = async () => {
    try {
      const payload = {
        restaurantId: vendorInfo.restaurantId,
        addonGroups: draftAddonGroups,
        menuCard: draftMenu,
        timeSlots: draftTimeSlots
      }

      let response
      if (!draftVersionId) {
        // Create new draft menu
        response = await axiosInstance.post(
          '/api/vendor/menu/create-draft',
          payload
        )
      } else {
        // Update existing draft menu
        response = await axiosInstance.put(
          `/api/vendor/menu/draft/${draftVersionId}`,
          payload
        )
      }

      if (response.data.responseCode === 0) {
        Modal.success({
          content: draftVersionId
            ? 'Draft Menu Updated Successfully'
            : 'Draft Menu Created Successfully'
        })
        // Update state with new version if created
        if (!draftVersionId && response.data.data?.versionId) {
          setDraftVersionId(response.data.data.versionId)
        }
        setDraftStatus('DRAFT')
        setRejectionReason(null)
      } else {
        Modal.error({
          title: 'Failed to Save Draft',
          content:
            response.data.message ||
            'An error occurred while saving the draft menu'
        })
      }
    } catch (error: any) {
      console.error('Error saving menu:', error)
      Modal.error({
        title: 'Error Saving Draft',
        content:
          error.response?.data?.message ||
          error.message ||
          'An unexpected error occurred'
      })
    }
  }

  const submitDraftForReview = async () => {
    if (!draftVersionId) {
      Modal.warning({
        title: 'No Draft to Submit',
        content: 'Please save your draft first before submitting for review.'
      })
      return
    }

    Modal.confirm({
      title: 'Submit Draft for Admin Review?',
      content:
        'Once submitted, you will not be able to edit this draft until admin approves or rejects it.',
      onOk: async () => {
        try {
          const response = await axiosInstance.post(
            `/api/vendor/menu/draft/${draftVersionId}/submit`
          )

          if (response.data.responseCode === 0) {
            Modal.success({
              title: 'Draft Submitted Successfully',
              content:
                'Your draft has been submitted for admin review. You will be notified once it is reviewed.'
            })
            setDraftStatus('PENDING_APPROVAL')
          } else {
            Modal.error({
              title: 'Failed to Submit Draft',
              content:
                response.data.message ||
                'An error occurred while submitting the draft menu'
            })
          }
        } catch (error: any) {
          console.error('Error submitting draft:', error)
          Modal.error({
            title: 'Error Submitting Draft',
            content:
              error.response?.data?.message ||
              error.message ||
              'An unexpected error occurred'
          })
        }
      }
    })
  }

  return (
    <div className='menu-main-container'>
      {modalVisible && (
        <AddModal
          visible={modalVisible}
          menuType={modalType}
          addCb={menuAdd}
          onCancel={handleCancel}
          categoryList={menu.map((menuItem: any) => {
            return { value: menuItem.category_id, label: menuItem.label }
          })}
          menu={menu}
        />
      )}
      <TimeSlotModal
        visible={modalTimeSlotVisible}
        onCancel={handleCancel}
        timeSlots={timeSlots}
        changeTimeSlots={changeTimeSlots}
      />
      <AddonGroupModal
        visible={modalAddonGroupVisible}
        onCancel={handleCancel}
        addonGroups={addonGroups}
        onChange={setAddonGroups}
        getAddonFoodItems={getAddonFoodItemsFromMenu}
        resolveFoodItem={resolveFoodItem}
      />

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key as 'live' | 'draft')
          setSelectedFood(null)
          setSelectedKeys([])
          setFoodList([])
        }}
        items={[
          {
            key: 'live',
            label: 'Live Menu'
          },
          {
            key: 'draft',
            label: 'Draft Menu'
          }
        ]}
        style={{ padding: '0 20px' }}
      />

      {/* Rejection Alert for Draft Tab */}
      {activeTab === 'draft' &&
        draftStatus === 'REJECTED' &&
        rejectionReason && (
          <Alert
            message='Draft Rejected'
            description={
              <div>
                <p>
                  <strong>Reason:</strong> {rejectionReason}
                </p>
                {liveMenu.length > 0 ? (
                  <Button
                    type='primary'
                    danger
                    onClick={clearDraftMenu}
                    style={{ marginTop: 10 }}
                  >
                    Clear & Clone from Live Menu
                  </Button>
                ) : (
                  <p style={{ marginTop: 10 }}>
                    You can edit your draft and resubmit for approval.
                  </p>
                )}
              </div>
            }
            type='error'
            showIcon
            closable
            onClose={() => setRejectionReason(null)}
            style={{ margin: '20px' }}
          />
        )}

      {/* Empty Draft Message */}
      {activeTab === 'draft' &&
        !draftVersionId &&
        draftStatus !== 'REJECTED' &&
        draftMenu.length === 0 && (
          <Alert
            message='No Draft Menu'
            description={
              <div>
                {liveMenu.length > 0 ? (
                  <>
                    <p>You must clone from the live menu to create a draft.</p>
                    <Button
                      type='primary'
                      onClick={cloneFromLiveMenu}
                      style={{ marginTop: 10 }}
                    >
                      Clone from Live Menu
                    </Button>
                  </>
                ) : (
                  <p>
                    You can start creating your menu from scratch. Click the "+"
                    button to add categories.
                  </p>
                )}
              </div>
            }
            type='info'
            showIcon
            style={{ margin: '20px' }}
          />
        )}

      {/* Disabled Items Tree View */}
      {activeTab === 'live' && disabledMenuTree.length > 0 && (
        <Card title={<span style={{ color: '#003eb3', fontSize: '16px', fontWeight: 'bold' }}>Manage Disabled Items</span>} style={{ margin: '20px', borderColor: '#d9d9d9', maxWidth: '650px', minWidth: '550px', backgroundColor: '#f4f7fb' }} styles={{ body: { padding: '8px 24px' } }}>
          <Tree
            style={{ backgroundColor: 'transparent' }}
            blockNode
            treeData={disabledMenuTree.map((cat: any) => ({
              key: `cat-${cat.category_id}`,
              title: (
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f0f0f0', width: '100%' }}>
                  <ShoppingCartOutlined style={{ fontSize: '18px', color: '#595959', marginRight: '16px' }} />
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <strong style={{ fontSize: '14px', color: '#000' }}>{cat.label}</strong>
                    <span style={{ fontSize: '12px', color: '#595959' }}>
                      {cat.hasSubCategory 
                        ? `${cat.children.reduce((acc: number, sub: any) => acc + (sub.foodItems?.length || 0), 0)} Items`
                        : `${cat.children.length} Items`
                      }
                    </span>
                  </div>
                  <Space size="middle" style={{ marginLeft: '16px' }}>
                    <Button size="small" style={{ color: '#0958d9', borderColor: '#91caff', backgroundColor: '#e6f4ff' }} icon={<CheckCircleOutlined />} onClick={(e) => {
                    e.stopPropagation()
                    const ids: string[] = []
                    if (cat.hasSubCategory) {
                      cat.children.forEach((sub: any) => sub.foodItems.forEach((f: any) => ids.push(f.food_id)))
                    } else {
                      cat.children.forEach((f: any) => ids.push(f.food_id))
                    }
                    updateBulkOrderStatus(ids, 'Available')
                  }}>Enable All</Button>
                  </Space>
                </div>
              ),
              children: cat.hasSubCategory 
                ? cat.children.map((sub: any) => ({
                    key: `sub-${sub.subcategory_id}`,
                    title: (
                      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f0f0f0', width: '100%' }}>
                        <ShoppingCartOutlined style={{ fontSize: '16px', color: '#595959', marginRight: '16px' }} />
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                          <strong style={{ fontSize: '14px', color: '#000' }}>{sub.label}</strong>
                          <span style={{ fontSize: '12px', color: '#595959' }}>{sub.foodItems?.length || 0} Items</span>
                        </div>
                        <Space size="middle" style={{ marginLeft: '16px' }}>
                          <Button size="small" style={{ color: '#0958d9', borderColor: '#91caff', backgroundColor: '#e6f4ff' }} icon={<CheckCircleOutlined />} onClick={(e) => {
                          e.stopPropagation()
                          updateBulkOrderStatus(sub.foodItems.map((f: any) => f.food_id), 'Available')
                        }}>Enable All</Button>
                        </Space>
                      </div>
                    ),
                    children: sub.foodItems.map((item: any) => ({
                      key: `item-${item.food_id}`,
                      title: (
                        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f0f0f0', width: '100%' }}>
                          <CoffeeOutlined style={{ fontSize: '16px', color: '#bfbfbf', marginRight: '16px' }} />
                          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <span style={{ fontSize: '14px', color: '#000' }}>{item.label}</span>
                            <span style={{ fontSize: '12px', color: '#595959' }}>${item.price?.toFixed(2) || '0.00'}</span>
                          </div>
                          <Button size="small" style={{ color: '#0958d9', borderColor: '#91caff' }} icon={<CheckCircleOutlined />} onClick={(e) => {
                            e.stopPropagation()
                            updateBulkOrderStatus([item.food_id], 'Available')
                          }}>Enable</Button>
                        </div>
                      )
                    }))
                  }))
                : cat.children.map((item: any) => ({
                    key: `item-${item.food_id}`,
                    title: (
                      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f0f0f0', width: '100%' }}>
                        <CoffeeOutlined style={{ fontSize: '16px', color: '#bfbfbf', marginRight: '16px' }} />
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                          <span style={{ fontSize: '14px', color: '#000' }}>{item.label}</span>
                          <span style={{ fontSize: '12px', color: '#595959' }}>${item.price?.toFixed(2) || '0.00'}</span>
                        </div>
                        <Button size="small" style={{ color: '#0958d9', borderColor: '#91caff' }} icon={<CheckCircleOutlined />} onClick={(e) => {
                          e.stopPropagation()
                          updateBulkOrderStatus([item.food_id], 'Available')
                        }}>Enable</Button>
                      </div>
                    )
                  }))
            }))}
            selectable={false}
          />
        </Card>
      )}

      {/* Show menu editor for both live and draft tabs */}
      {(activeTab === 'live' ||
        (activeTab === 'draft' &&
          (draftMenu.length > 0 || liveMenu.length === 0))) && (
          <MenuContext.Provider value={{ menu, timeSlots, addonGroups }}>
            <div className='menu-container-flex'>
              <div className='menu-left-column'>
                <div className='menu-continer-sidebar'>
                  <Layout
                    hasSider
                    style={{ flex: 1, background: 'white', textAlign: 'center' }}
                  >
                    <Sider
                      style={siderStyle}
                      width={'300px'}
                      breakpoint='md'
                      collapsedWidth='0'
                      onBreakpoint={(broken) => {
                        console.log(broken)
                      }}
                      onCollapse={(collapsed, type) => {
                        console.log(collapsed, type)
                      }}
                    >
                      <ScrollablePanel style={{ height: '100%' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            gap: '10px',
                            alignItems: 'center'
                          }}
                        >
                          <h2 style={{ color: 'white', textWrap: 'nowrap' }}>
                            Category{' '}
                          </h2>
                          <div
                            style={{
                              color: 'white',
                              fontSize: '24px',
                              cursor: 'pointer',
                              pointerEvents: canEdit ? 'auto' : 'none'
                            }}
                            onClick={(event) => {
                              event.stopPropagation()
                              add(event, 'Category', '', '')
                            }}
                          >
                            <PlusCircleTwoTone twoToneColor='#34d970' />
                          </div>
                        </div>

                        <Menu
                          selectedKeys={selectedKeys}
                          style={{ width: '300px', fontSize: '20px' }}
                          mode='inline'
                          defaultSelectedKeys={['1']}
                          defaultOpenKeys={['sub1']}
                          items={menu.map((item: any) => ({
                            key: item.key,
                            label: (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
                                <Tooltip title={item.label} placement="topLeft">
                                  <span style={{ 
                                    maxWidth: '120px', 
                                    whiteSpace: 'nowrap', 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    display: 'inline-block',
                                    verticalAlign: 'middle'
                                  }}>
                                    {item.label}
                                  </span>
                                </Tooltip>
                                {activeTab === 'live' && (
                                  <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0, marginLeft: '8px' }}>
                                    <ConfigProvider theme={{ components: { Switch: { handleBg: 'green' } } }}>
                                      <Switch
                                        size="small"
                                        style={{ background: 'white' }}
                                        checkedChildren={<CheckOutlined style={{ color: 'green' }} />}
                                        unCheckedChildren={<CloseOutlined style={{ color: 'red' }} />}
                                        checked={isCategoryAvailable(item.category_id)}
                                        onClick={(checked, event) => toggleCategoryStatus(event, item.category_id, !checked)}
                                      />
                                    </ConfigProvider>
                                  </div>
                                )}
                              </div>
                            ),
                            disabled: selectedFood ? true : false,
                            icon: (
                              <>
                                <Space>
                                  <div
                                    style={{
                                      pointerEvents: canEdit ? 'auto' : 'none'
                                    }}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      add(
                                        event,
                                        'SubCategory/FoodItem',
                                        item.category_id,
                                        ''
                                      )
                                    }}
                                  >
                                    <PlusCircleTwoTone
                                      style={{ fontSize: '24px' }}
                                      twoToneColor='#356975'
                                    />
                                  </div>
                                  <div
                                    style={{
                                      pointerEvents: canEdit ? 'auto' : 'none'
                                    }}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      deleteMenuItems(
                                        event,
                                        'Category',
                                        item.category_id,
                                        '',
                                        ''
                                      )
                                    }}
                                  >
                                    <MinusCircleTwoTone
                                      style={{ fontSize: '24px' }}
                                      twoToneColor='#d43d13'
                                    />
                                  </div>
                                </Space>
                              </>
                            ),
                            children:
                              item.hasSubCategory &&
                              item.children?.map((subItem: any) => ({
                                icon: (
                                  <Space>
                                    <div
                                      style={{
                                        pointerEvents: canEdit ? 'auto' : 'none'
                                      }}
                                      onClick={(event) => {
                                        event.stopPropagation()
                                        add(
                                          event,
                                          'FoodItem',
                                          item.category_id,
                                          subItem.subcategory_id
                                        )
                                      }}
                                    >
                                      <PlusCircleTwoTone
                                        style={{ fontSize: '24px' }}
                                        twoToneColor='#356975'
                                      />
                                    </div>
                                    <div
                                      style={{
                                        pointerEvents: canEdit ? 'auto' : 'none'
                                      }}
                                      onClick={(event) => {
                                        event.stopPropagation()
                                        deleteMenuItems(
                                          event,
                                          'SubCategory',
                                          item.category_id,
                                          subItem.subcategory_id,
                                          ''
                                        )
                                      }}
                                    >
                                      <MinusCircleTwoTone
                                        style={{ fontSize: '24px' }}
                                        twoToneColor='#d43d13'
                                      />
                                    </div>
                                  </Space>
                                ),
                                key: subItem.key,
                                disabled: selectedFood ? true : false,
                                label: (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
                                    <Tooltip title={subItem.label} placement="topLeft">
                                      <span style={{ 
                                        maxWidth: '110px', 
                                        whiteSpace: 'nowrap', 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis', 
                                        display: 'inline-block',
                                        verticalAlign: 'middle'
                                      }}>
                                        {subItem.label}
                                      </span>
                                    </Tooltip>
                                    {activeTab === 'live' && (
                                      <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0, marginLeft: '8px' }}>
                                        <ConfigProvider theme={{ components: { Switch: { handleBg: 'green' } } }}>
                                          <Switch
                                            size="small"
                                            style={{ background: 'white' }}
                                            checkedChildren={<CheckOutlined style={{ color: 'green' }} />}
                                            unCheckedChildren={<CloseOutlined style={{ color: 'red' }} />}
                                            checked={isSubCategoryAvailable(item.category_id, subItem.subcategory_id)}
                                            onClick={(checked, event) => toggleSubCategoryStatus(event, item.category_id, subItem.subcategory_id, !checked)}
                                          />
                                        </ConfigProvider>
                                      </div>
                                    )}
                                  </div>
                                )
                              }))
                          }))}
                          theme='dark'
                          onClick={categorySelected}
                        />
                      </ScrollablePanel>
                    </Sider>
                  </Layout>
                  {foodList.length > 0 && (
                    <Layout hasSider style={{ flex: 1, background: 'white' }}>
                      <Sider
                        style={foodSiderStyle}
                        width={'300px'}
                        breakpoint='md'
                        collapsedWidth='0'
                        onBreakpoint={(broken) => {
                          console.log(broken)
                        }}
                        onCollapse={(collapsed, type) => {
                          console.log(collapsed, type)
                        }}
                      >
                        <ScrollablePanel style={{ height: '100%' }}>
                          <h2 style={{ color: 'white', textAlign: 'center' }}>
                            FoodItem
                          </h2>
                          <Menu
                            style={{
                              width: '300px',
                              fontSize: '20px',
                              textAlign: 'left'
                            }}
                            // defaultSelectedKeys={['1']}
                            // defaultOpenKeys={['sub1']}
                            items={foodList.map((foodItem: any) => ({
                              // icon: <div onClick={() => deleteMenuItems('FoodItem', item.category_id, item.subcategory_id, foodItem.food_id)}><MinusCircleTwoTone style={{ fontSize: '24px' }} twoToneColor="#d43d13" /></div>
                              key: foodItem.key,
                              label: (
                                <Tooltip title={foodItem.label} placement="topLeft">
                                  <span style={{ 
                                    color: foodItem.status === 'Not Available' ? '#ff4d4f' : 'inherit',
                                    maxWidth: '130px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: 'inline-block',
                                    verticalAlign: 'middle'
                                  }}>
                                    {foodItem.label}
                                    {foodItem.status === 'Not Available' && (
                                      <span style={{ fontSize: '12px', marginLeft: '5px' }}>(Disabled)</span>
                                    )}
                                    {foodItem.isAddon && (
                                      <Badge
                                        count='ADDON'
                                        style={{
                                          backgroundColor: '#722ed1',
                                          marginLeft: 8,
                                          fontSize: 10
                                        }}
                                      />
                                    )}
                                  </span>
                                </Tooltip>
                              ),
                              disabled: selectedFood ? true : false,
                              icon: (
                                <Space>
                                  {activeTab === 'live' && (
                                    <div
                                      onClick={(event) => {
                                        event.stopPropagation()
                                        updateOrderStatus(
                                          event,
                                          'FoodItem',
                                          foodItem.category_id,
                                          foodItem.subcategory_id,
                                          foodItem.food_id
                                        )
                                      }}
                                    >
                                      <ConfigProvider
                                        theme={{
                                          components: {
                                            Switch: {
                                              handleBg: 'green'
                                            }
                                          }
                                        }}
                                      >
                                        <Switch
                                          style={{ background: 'white' }}
                                          checkedChildren={
                                            <CheckOutlined
                                              style={{ color: 'green' }}
                                            />
                                          }
                                          unCheckedChildren={
                                            <CloseOutlined
                                              style={{ color: 'red' }}
                                            />
                                          }
                                          size='small'
                                          disabled={selectedFood ? true : false}
                                          checked={
                                            foodItem.status === 'Available'
                                          }
                                        />
                                      </ConfigProvider>
                                    </div>
                                  )}
                                  <div
                                    style={{
                                      pointerEvents: canEdit ? 'auto' : 'none'
                                    }}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      deleteMenuItems(
                                        event,
                                        'FoodItem',
                                        foodItem.category_id,
                                        foodItem.subcategory_id,
                                        foodItem.food_id
                                      )
                                    }}
                                  >
                                    <MinusCircleTwoTone
                                      style={{ fontSize: '24px' }}
                                      twoToneColor='#d43d13'
                                    />
                                  </div>
                                </Space>
                              )
                            }))}
                            theme='dark'
                            onClick={foodSelected}
                          />
                        </ScrollablePanel>
                      </Sider>
                    </Layout>
                  )}
                </div>
                <div className='menu-actions'>
                  <div
                    className={`timeSlotButton ${modalAddonGroupVisible ? 'active' : ''}`}
                    onClick={() => setModalAddonGroupVisible(true)}
                    style={{
                      cursor: 'pointer'
                    }}
                  >
                    Manage Addon Groups
                  </div>
                  <div
                    className={`timeSlotButton ${modalTimeSlotVisible ? 'active' : ''}`}
                    onClick={() => setModalTimeSlotVisible(true)}
                  >
                    Time Slots
                  </div>
                  {activeTab === 'draft' && (
                    <div style={{ flexDirection: 'column', display: 'flex', gap: '8px' }}>
                      <Button
                        disabled={!canEdit}
                        onClick={updateMenuCard}
                        style={{
                          background: '#2f8e46',
                          color: 'white',
                          fontWeight: 'bold',
                          padding: '10px',
                          width: '300px'
                        }}
                      >
                        {!draftVersionId ? 'Create Draft' : 'Save Draft'}
                      </Button>
                      {draftVersionId && draftStatus === 'DRAFT' && (
                        <Button
                          type='primary'
                          onClick={submitDraftForReview}
                          style={{
                            background: '#1890ff',
                            fontWeight: 'bold',
                            padding: '10px',
                            width: '300px'
                          }}
                        >
                          Submit for Admin Review
                        </Button>
                      )}
                      {draftStatus === 'PENDING_APPROVAL' && (
                        <Alert
                          message='Pending Admin Review'
                          description='Your draft is currently being reviewed by an admin.'
                          type='warning'
                          showIcon
                          style={{ width: '300px' }}
                        />
                      )}
                    </div>
                  )}
                  {activeTab === 'live' && (
                    <Alert
                      message='Live Menu (Read Only)'
                      description='You are viewing the live menu. Switch to Draft tab to make changes.'
                      type='info'
                      showIcon
                      style={{ width: '300px' }}
                    />
                  )}
                  {activeTab === 'draft' && isRestaurantActive && (
                    <Alert
                      message='Draft Editing Disabled'
                      description='Draft menu cannot be edited while the restaurant is live. Please close the restaurant to make changes.'
                      type='warning'
                      showIcon
                      style={{ width: '300px' }}
                    />
                  )}
                </div>
              </div>

              <Layout
                style={{ flex: 6, boxShadow: '1px 1px 6px 2px #80808073' }}
                className={selectedFood ? '' : 'hidden'}
              >
                <Badge.Ribbon text='Food Info' placement='start' color='cyan'>
                  <div
                    style={{
                      padding: 24,
                      textAlign: 'center',
                      background: colorBgContainer,
                      borderRadius: borderRadiusLG
                    }}
                  >
                    <Content
                      style={{ position: 'relative', overflow: 'initial' }}
                    >
                      {selectedFood && (
                        <FoodItem
                          foodItemList={selectedFood}
                          vendorInfo={vendorInfo}
                          onUpdate={onUpdate}
                          getAddOns={getAddOns}
                          checkAddonUsage={checkAddonUsage}
                          isLive={activeTab === 'live'}
                        />
                      )}
                    </Content>
                  </div>
                </Badge.Ribbon>
              </Layout>
            </div>
          </MenuContext.Provider>
        )}
    </div>
  )
}

export default MenuPage
